// Line item that isn't in the checkout yet
type LineItem = {
  variantId: string
  quantity: number
}

// Line item from checkout
type CheckoutLineItem = {
  variant: {
    id: string
  }
  quantity: number
  id: string
}

type LineItemAction = LineItemAdd | LineItemUpdate | LineItemRemove

export type LineItemAdd = {
  action: 'add'
  variantId: string
  quantity: number
  subscription?: any
}

export type LineItemUpdate = {
  action: 'update'
  id: string
  quantity: number
}

export type LineItemRemove = {
  action: 'remove'
  id: string
}

export const REWARDS: Record<string, number> = {
  'free-canvas-poop-bag-holder': 50,
  'free-paw-hero': 75,
  '$25-cash-back': 100
}
/**
Given current checkout, and line item action, return an array of line item actions for rewards, categorized by add, update, remove
 */
export function generateRewardLineItems(
  checkout: any,
  productMap: any,
  lineItemAction: LineItemAction,
  actualTotal: number
) {
  // If the line item action is a reward, return empty arrays
  // because we don't want to calculate rewards for rewards
  const isReward = isLineItemActionReward(checkout, productMap, lineItemAction)
  if (isReward) return [[], [], []]

  // Calculate target rewards state
  const rewardItems = calculateRewards({ checkout, productMap, lineItemAction, actualTotal })

  // Calculate delta rewards
  const deltaRewards = calculateDeltaRewards(rewardItems, checkout.lineItems, productMap)
  const rewardAdds = deltaRewards.filter(isLineItemAdd).map(({ variantId, quantity }) => ({
    variantId,
    quantity
  }))
  const rewardUpdates = deltaRewards.filter(isLineItemUpdate).map(({ id, quantity }) => ({
    id,
    quantity
  }))
  const rewardRemoves = deltaRewards.filter(isLineItemRemove).map(({ id }) => id)
  // return all of the adds/updates/deletes
  return [rewardAdds, rewardUpdates, rewardRemoves]
}

export function isLineItemActionReward(
  checkout: any,
  productMap: any,
  lineItemAction: LineItemAction
) {
  if (lineItemAction.action === 'add') {
    const variantId = extractVariantNumber(lineItemAction.variantId)
    const slug = productMap?.byVariantId?.[variantId]?.slug
    return Boolean(REWARDS[slug])
  } else {
    const lineItem = checkout?.lineItems?.find((item) => item.id === lineItemAction.id)
    if (lineItem) {
      const variantId = extractVariantNumber(lineItem.variant.id)
      const slug = productMap?.byVariantId?.[variantId]?.slug
      return Boolean(REWARDS[slug])
    }
    return false
  }
}

export function calculateRewards({
  checkout,
  productMap,
  lineItemAction,
  actualTotal
}: {
  checkout: any
  productMap: any
  lineItemAction: LineItemAction
  actualTotal: number
}) {
  const currentSubtotal = actualTotal || 0

  let deltaPrice = 0
  if (isLineItemAdd(lineItemAction)) {
    deltaPrice = priceDeltaAdd(lineItemAction, productMap)
  } else if (isLineItemRemove(lineItemAction)) {
    deltaPrice = priceDeltaRemove(lineItemAction, checkout)
  } else if (isLineItemUpdate(lineItemAction)) {
    deltaPrice = priceDeltaUpdate(lineItemAction, checkout)
  }

  const projectedSubtotal = currentSubtotal + deltaPrice

  const rewardItems: { variantId: string; quantity: number }[] = []
  Object.entries(REWARDS).forEach(([rewardSlug, minimumSpend]) => {
    if (projectedSubtotal >= minimumSpend) {
      const variantId = productMap.bySlug[rewardSlug].single.variantId.encoded
      rewardItems.push({ variantId, quantity: 1 })
    }
  })
  return rewardItems
}

function priceDeltaAdd(lineItemAdd: LineItemAdd, productMap: any) {
  const { variantId, quantity, subscription } = lineItemAdd
  const priceType = subscription ? 'subscription' : 'oneTime'
  const unitPrice = productMap?.byVariantId?.[variantId]?.pricing?.[priceType]?.price || 0
  return unitPrice * quantity
}

function priceDeltaRemove(lineItemRemove: LineItemRemove, checkout: any) {
  const { id } = lineItemRemove
  const found = checkout?.lineItems?.find((item) => item.id === id)
  if (!found) return 0
  const { quantity, variant, customAttributes } = found
  const discountFactor = customAttributes?.find((attr) => attr.key === 'selling_plan_frequency')
    ? 0.85
    : 1
  const unitPrice = Number(variant?.priceV2?.amount) * discountFactor || 0
  return -1 * unitPrice * quantity
}

function priceDeltaUpdate(lineItemUpdate: LineItemUpdate, checkout: any) {
  const { id, quantity } = lineItemUpdate
  const found = checkout?.lineItems?.find((item) => item.id === id)
  if (!found) return 0
  const { variant, customAttributes } = found
  const discountFactor = customAttributes?.find((attr) => attr.key === 'selling_plan_frequency')
    ? 0.85
    : 1
  const unitPrice = Number(variant?.priceV2?.amount) * discountFactor || 0
  const oldQuantity = found.quantity
  return unitPrice * (quantity - oldQuantity)
}

/**
 * Given a "current state" lineitems and a "target state" lineitems
 * return an array of actions to take to get from current to target
 */
export function calculateDeltaRewards(
  targetState: LineItem[],
  currentState: CheckoutLineItem[],
  productMap: unknown
): LineItemAction[] {
  let result: LineItemAction[] = []

  targetState.forEach((targetItem) => {
    let found = currentState.find(
      (currentItem) =>
        extractVariantNumber(currentItem.variant.id) === extractVariantNumber(targetItem.variantId)
    )

    if (!found) {
      // Adding lineitems use variantId
      result.push({ action: 'add', variantId: targetItem.variantId, quantity: targetItem.quantity })
    } else if (found.quantity !== targetItem.quantity) {
      // Update lineitems require the lineitem id
      result.push({
        action: 'update',
        id: found.id,
        quantity: targetItem.quantity
      })
    }
  })

  currentState.forEach((currentItem) => {
    let found = targetState.find(
      (targetItem) =>
        extractVariantNumber(targetItem.variantId) === extractVariantNumber(currentItem.variant.id)
    )
    const slug = productMap.byVariantId[extractVariantNumber(currentItem.variant.id)].slug
    // If item is not in target rewards state AND the item is a reward, remove it
    if (!found && REWARDS[slug]) {
      result.push({ action: 'remove', id: currentItem.id })
    }
  })

  return result
}
export function isLineItemAdd(action: LineItemAction): action is LineItemAdd {
  return action.action === 'add'
}

export function isLineItemUpdate(action: LineItemAction): action is LineItemUpdate {
  return action.action === 'update'
}
export function isLineItemRemove(action: LineItemAction): action is LineItemRemove {
  return action.action === 'remove'
}

export function extractVariantNumber(variantId: string) {
  const parts = variantId.split('/')
  const idPart = parts[parts.length - 1]
  const id = idPart.split('?')[0]
  return id
}
